import { c as _c } from "react/compiler-runtime";
import { IcoInfo16, type Icon } from '@onefootprint/icons';
import { CodeInline, LinkButton, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import { getCustomDIs } from '@/entities/utils/get-dis';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useField from '../../hooks/use-field';
import type { DiField } from '../../vault.types';
import Field from '../field';
import ShowMoreButton from './components/show-more-button';
import useCustomFieldPlaybookPrompt from './hooks/use-custom-field-playbook-prompt';
type CustomDataFieldsProps = WithEntityProps & {
  iconComponent: Icon;
  title: string;
};
const CustomDataFields = t0 => {
  const $ = _c(28);
  const {
    entity,
    iconComponent: IconComponent,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const [showAllCta, setShowAllCta] = useState(false);
  const decryptControls = useDecryptControls();
  const decryptForm = useDecryptForm();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== decryptForm.control) {
    t3 = {
      control: decryptForm.control,
      name: "fields",
      defaultValue: t2
    };
    $[2] = decryptForm.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedFields = useWatch(t3);
  const getPrompt = useCustomFieldPlaybookPrompt(entity.id);
  const getFieldProps = useField(entity);
  let t4;
  if ($[4] !== entity.data || $[5] !== entity.id) {
    t4 = {
      entityAttributes: entity.data,
      entityId: entity.id
    };
    $[4] = entity.data;
    $[5] = entity.id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: vaultWithTransforms
  } = useEntityVault(t4);
  let t5;
  if ($[7] !== IconComponent || $[8] !== decryptControls.step || $[9] !== decryptForm || $[10] !== entity || $[11] !== getFieldProps || $[12] !== getPrompt || $[13] !== selectedFields || $[14] !== showAllCta || $[15] !== t || $[16] !== title || $[17] !== vaultWithTransforms) {
    const {
      vault: vaultData
    } = vaultWithTransforms || {};
    const customDIs = getCustomDIs(vaultData || {}, entity.data);
    let t6;
    if ($[19] !== getFieldProps) {
      t6 = di => getFieldProps(di).canSelect;
      $[19] = getFieldProps;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    const selectableFields = customDIs.filter(t6);
    let t7;
    if ($[21] !== selectedFields) {
      t7 = field => selectedFields.includes(field);
      $[21] = selectedFields;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    const allSelected = selectableFields.every(t7);
    const shouldShowSelectAll = decryptControls.step === "selecting-fields" && selectableFields.length > 0;
    const shouldShowShowMore = customDIs.length > 5;
    const initialFields = customDIs.slice(0, 5);
    const additionalFields = customDIs.slice(5);
    const handleSelectAll = () => {
      decryptForm.setFields(selectableFields);
    };
    const handleDeselectAll = () => {
      decryptForm.unsetFields(selectableFields);
    };
    let t8;
    if ($[23] !== getPrompt) {
      t8 = field_0 => {
        const prompt = getPrompt(field_0.di);
        return prompt ? <div className="flex gap-2 items-center"><CodeInline disabled={true}>{field_0.di}</CodeInline><Tooltip content={prompt}><IcoInfo16 color="tertiary" /></Tooltip></div> : <CodeInline disabled={true}>{field_0.di}</CodeInline>;
      };
      $[23] = getPrompt;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    const renderLabel = t8;
    let t9;
    if ($[25] !== entity || $[26] !== renderLabel) {
      t9 = field_1 => <div key={field_1.di}><Field key={field_1.di} renderLabel={() => renderLabel(field_1)} di={field_1.di} entity={entity} /></div>;
      $[25] = entity;
      $[26] = renderLabel;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    const renderField = t9;
    t5 = vaultData ? <div className="flex overflow-hidden flex-col w-full h-full rounded border border-solid border-tertiary"><div className="flex justify-between px-5 py-2 rounded-t-md border-b border-solid border-tertiary bg-secondary"><div className="flex gap-2 items-center"><IconComponent /><div className="text-label-2 text-primary">{title}</div></div>{shouldShowSelectAll && <LinkButton onClick={allSelected ? handleDeselectAll : handleSelectAll}>{allSelected ? t(_temp) : t(_temp2)}</LinkButton>}</div><div className={cx("flex flex-col gap-3 p-5", {
        "pb-0": shouldShowShowMore
      })}><div className="w-full"><div className="flex flex-col gap-3"><div className="flex flex-col gap-3">{initialFields.map(di_0 => renderField({
                di: di_0
              }))}</div><div className={cx("flex flex-col gap-3", {
              hidden: !showAllCta
            })}>{additionalFields.map(di_1 => renderField({
                di: di_1
              }))}</div></div></div>{shouldShowShowMore && <ShowMoreButton showAllCta={showAllCta} onClick={() => setShowAllCta(!showAllCta)} count={`${t(_temp3, {
          count: customDIs.length - 5
        })}`}>{showAllCta ? t(_temp4) : t(_temp5)}</ShowMoreButton>}</div></div> : null;
    $[7] = IconComponent;
    $[8] = decryptControls.step;
    $[9] = decryptForm;
    $[10] = entity;
    $[11] = getFieldProps;
    $[12] = getPrompt;
    $[13] = selectedFields;
    $[14] = showAllCta;
    $[15] = t;
    $[16] = title;
    $[17] = vaultWithTransforms;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
export default CustomDataFields;
function _temp($) {
  return $.deselectAll;
}
function _temp2($_0) {
  return $_0.selectAll;
}
function _temp3($_1) {
  return $_1.actions.moreFields;
}
function _temp4($_2) {
  return $_2.actions.showLess;
}
function _temp5($_3) {
  return $_3.actions.showAll;
}